<template>
  <a-config-provider :theme="antdTheme" :locale="locale" :key="isMobileAgent">
    <!-- app.vue root -->
    <div class="w-full h-full">
      <Suspense>
        <template #default><router-view :key="$route.fullPath" /></template>
        <!-- 异步组件加载前的骨架/Spinner -->
        <template #fallback>
          <a-spin size="large" tip="加载中…" />
        </template>
      </Suspense>
    </div>
    <!-- 广告 -->
    <AdvertisingModal />
    <ModalHost />
    <!-- 打开APP -->
    <div
      v-if="
        isMobile && isMobileShow && route.name != 'RealnameSuccess' && route.name != 'RealnameFail'
      "
      class="fixed bottom-[0px] left-[0px] right-[0px] pt-[10px] h-[74px] pb-[24px] w-full bg-ground-0 z-[9999999999] flex justify-center items-center gap-[24px]"
    >
      <GtcButton type="primary" mode="light" @click="isMobileShow = false"> 继续访问 </GtcButton>

      <GtcButton type="primary" @click="openApp"> 打开APP </GtcButton>
    </div>
  </a-config-provider>
</template>

<script setup lang="tsx">
import { computed, ref, h, onMounted } from 'vue'
import { message, theme } from 'ant-design-vue'
import { useI18n } from 'vue-i18n'
import { RouterView, useRoute } from 'vue-router'
import { useThemeStore } from '@/store'
import { ModalHost } from '@/components'
import { notification } from 'ant-design-vue'
import AdvertisingModal from '@/modals/common/AdvertisingModal.vue'
import GtcButton from '@/components/gtc-button/GtcButton.vue'
import GtcNoticeSuccess from '@/components/gtc-notice/GtcNoticeSuccess.vue'
import GtcNoticeError from '@/components/gtc-notice/GtcNoticeError.vue'
import GtcNoticeWarning from '@/components/gtc-notice/GtcNoticeWarning.vue'
import zhCN from 'ant-design-vue/es/locale/zh_CN'
import dayjs from 'dayjs'
import 'dayjs/locale/zh-cn'
import { useAppBootstrap } from '@/composables'
import { useWindowSize } from '@vueuse/core'
import { getCssVar } from './utils/charts'

const { width } = useWindowSize()
const isMobileAgent = computed(() => width.value < 1024)
const route = useRoute()
const isMobile = ref(false)
const isMobileShow = ref(true)

const openApp = (action = '') => {
  let paramsStr = Object.keys(route.query)
    .map((key) => `${key}=${route.query[key]}`)
    .join('&')

  if (window.location.href.includes('fx=') && window.location.href.includes('email_sha256=')) {
    paramsStr += `&action=login`
  } else if (
    window.location.href.includes('email=') &&
    window.location.href.includes('name=') &&
    window.location.href.includes('platform=') &&
    window.location.href.includes('bind_id=')
  ) {
    paramsStr += `&action=register`
  } else if (window.location.href.includes('gtc-copy') && window.location.href.includes('uuid=')) {
    paramsStr += `&action=gtccopy`
  } else if (
    window.location.href.includes('in-platform-copy') &&
    window.location.href.includes('uuid=')
  ) {
    paramsStr += `&action=inplatformcopy`
  } else if (
    window.location.href.includes('out-platform-copy') &&
    window.location.href.includes('uuid=')
  ) {
    paramsStr += `&action=outplatformcopy`
  } else if (
    window.location.href.includes('pamm-signal') &&
    window.location.href.includes('uuid=')
  ) {
    paramsStr += `&action=pamm`
  } else {
    paramsStr += `&action=default`
  }

  // React Native WebView
  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(paramsStr)
  } else {
    window.location.href = `gtcapp://Quotes?${paramsStr}`
    // isMobileDownload.value = true;
    // 判断是IOS还是Android
    if (
      navigator.userAgent.includes('iPhone') ||
      navigator.userAgent.includes('iPad') ||
      navigator.userAgent.includes('iPod')
    ) {
      window.location.href = 'https://www.baidu.com' // 替换成苹果应用商店
    } else {
      window.location.href = 'https://www.baidu.com' // 替换成安卓应用商店
    }
  }
}

dayjs.locale('zh-cn')
useAppBootstrap()
const themeStore = useThemeStore()

const { t } = useI18n()
const locale = computed(() => ({
  ...zhCN,
  Pagination: {
    ...(zhCN as any).Pagination,
    items_per_page: t('components.gtc-table.itemsPerPage'),
    jump_to: t('components.gtc-table.jumpTo'),
    jump_to_confirm: t('components.gtc-table.jumpToConfirm'),
    page: t('components.gtc-table.page'),
  },
}))
const patchBgAlgorithm = (seedToken: Record<string, string>, mapToken: Record<string, string>) => {
  return {
    ...seedToken,
    ...mapToken,
    colorBgContainer: 'var(--ground-0)',
  }
}

const antdTheme = computed(() => {
  const isDark = themeStore.theme === 'dark'
  return {
    algorithm: isDark ? [theme.darkAlgorithm, patchBgAlgorithm] : theme.defaultAlgorithm,
    token: {
      colorPrimary: getCssVar('--sec-500'),
      borderRadius: 6,
      fontSize: 14,
    },
    // 常用组件的细粒度 Token，这里仅给出示范，可按需扩展 / 覆盖
    components: {
      Button: {
        colorPrimary: 'var(--sec-500)',
        colorPrimaryHover: 'var(--sec-700)',
        colorPrimaryActive: 'var(--sec-300)',
        controlHeight: 40,
        borderRadius: 6,
        paddingInline: 16,
        fontWeight: 500,
        colorLink: 'var(--sec-500)',
        colorLinkActive: 'var(--sec-700)',
        colorLinkHover: 'var(--sec-300)',
      },
      Input: {
        controlHeight: 30,
        paddingSM: 8,
        padding: 12,
        paddingMD: 8,
        paddingLG: 8,
        colorBorder: 'var(--rim-300)',
        colorPrimaryHover: 'var(--ink-400)',
        colorPrimary: 'var(--blue-primary)',
      },
      Select: {
        colorBorder: 'var(--rim-300)',
        colorPrimaryHover: 'var(--ink-400)',
        colorPrimary: 'var(--blue-primary)',
      },
      DatePicker: {
        colorBorder: 'var(--rim-300)',
        colorPrimaryHover: 'var(--ground-300)',
        // colorPrimary: 'var(--blue-500)',
      },
      Pagination: {
        colorPrimary: 'var(--blue-primary)',
        colorPrimaryHover: 'var(--ink-400)',
        colorBorder: 'var(--rim-300)',
      },
      Table: {},
      Modal: {},
      Form: {},
    },
  }
})

// 自定义全局message
message.config({
  prefixCls: 'gtc-message',
})

const GtcNotification = (type = 'info', title = '', message = '', key?: string) => {
  const duration = 3
  const baseConfig = {
    placement: 'bottomLeft' as const,
    bottom: '50px',
    duration: duration,
    message: title,
    description: message,
    style: {
      width: '360px',
    },
    ...(key ? { key } : {}), // 如果提供了 key，则添加到配置中
  }

  if (type === 'success') {
    notification.open({
      ...baseConfig,
      icon: () => h(GtcNoticeSuccess),
    })
  }
  if (type === 'warning') {
    notification.open({
      ...baseConfig,
      icon: () => h(GtcNoticeWarning),
    })
  }
  if (type === 'error') {
    notification.open({
      ...baseConfig,
      icon: () => h(GtcNoticeError),
    })
  }
  if (type === 'info') {
    notification.open(baseConfig)
  }
}

window.GtcNotification = GtcNotification

onMounted(() => {
  setTimeout(() => {
    isMobile.value =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Windows Phone|Mobile/i.test(
        navigator.userAgent,
      )
  }, 1500)
})
</script>

<style lang="less">
// ant组件样式全局处理
@import './assets/styles/ant_customer.less';
</style>
